import logger from '../utils/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Flame } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CandleWall = ({ programId, programName }) => {
  const [candles, setCandles] = useState([]);
  const [candleCount, setCandleCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', message: '', visibility: 'public', donation_amount: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeCandle, setActiveCandle] = useState(null);
  const tooltipRef = useRef(null);

  const fetchCandles = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/candles/${programId}`);
      setCandles(res.data.candles || []);
      setCandleCount(res.data.count || 0);
    } catch (err) {
      logger.error('Error fetching candles:', err);
    }
  }, [programId]);

  useEffect(() => {
    fetchCandles();
  }, [fetchCandles]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setActiveCandle(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const lightCandle = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/candles`, {
        program_id: programId,
        ...form
      });
      setSubmitted(true);
      fetchCandles();
      setTimeout(() => {
        setShowModal(false);
        setSubmitted(false);
        setForm({ name: '', message: '', visibility: 'public', donation_amount: 0 });
      }, 2000);
    } catch (err) {
      logger.error('Error lighting candle:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getCandleStyle = (type) => {
    switch (type) {
      case 'golden': return 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.7)]';
      case 'bright': return 'text-orange-400 drop-shadow-[0_0_5px_rgba(251,146,60,0.5)]';
      default: return 'text-yellow-300 drop-shadow-[0_0_3px_rgba(253,224,71,0.4)]';
    }
  };

  return (
    <section className="py-10 bg-gradient-to-b from-[#1a1207] to-[#0A0A0A]" data-testid="candle-wall-section">
      <div className="px-6">
        {/* Header */}
        <div className="text-center mb-6">
          <Flame className="w-7 h-7 text-amber-400 mx-auto mb-2 animate-pulse" />
          <h2 className="font-heading text-2xl font-bold text-white mb-1">
            Candle Wall
          </h2>
          <p className="text-sm text-gray-400">
            {candleCount > 0
              ? `${candleCount} candle${candleCount !== 1 ? 's' : ''} lit for ${programName}`
              : `Be the first to light a candle for ${programName}`
            }
          </p>
        </div>

        {/* Light a Candle Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowModal(true)}
            className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-3 rounded-full font-semibold text-sm hover:from-amber-500 hover:to-amber-400 transition-all shadow-lg shadow-amber-900/30"
            data-testid="light-candle-btn"
          >
            <span className="text-lg">🕯️</span>
            Light a Candle
            <span className="absolute -inset-1 rounded-full bg-amber-400/20 animate-ping opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>

        {/* Candle Grid */}
        {candles.length > 0 && (
          <div className="max-w-2xl mx-auto relative">
            <div className="flex flex-wrap justify-center gap-2" data-testid="candle-grid">
              {candles.map((candle, i) => (
                <div
                  key={candle.id}
                  className="relative group cursor-pointer"
                  onClick={() => setActiveCandle(activeCandle === candle.id ? null : candle.id)}
                  style={{ animationDelay: `${i * 50}ms` }}
                  data-testid={`candle-${candle.id}`}
                >
                  <div className={`text-2xl sm:text-3xl transition-transform duration-300 hover:scale-125 ${getCandleStyle(candle.candle_type)}`}
                    style={{ animation: `flicker ${2 + Math.random() * 2}s ease-in-out infinite alternate` }}
                  >
                    🕯️
                  </div>

                  {/* Tooltip on click */}
                  {activeCandle === candle.id && (candle.name || candle.message) && (
                    <div
                      ref={tooltipRef}
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#1C1C1C] border border-amber-900/30 rounded-lg p-3 min-w-[180px] max-w-[240px] z-10 shadow-xl"
                      data-testid={`candle-tooltip-${candle.id}`}
                    >
                      <div className="text-amber-400 text-xs font-semibold mb-1">
                        {candle.name || 'Anonymous'}
                      </div>
                      {candle.message && (
                        <p className="text-gray-300 text-xs leading-relaxed">
                          "{candle.message}"
                        </p>
                      )}
                      {candle.candle_type === 'golden' && (
                        <span className="text-[10px] text-amber-500 mt-1 block">Family Sponsor</span>
                      )}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1C1C1C] border-r border-b border-amber-900/30 rotate-45 -mt-1"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-4 mt-6 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="text-yellow-300">🕯️</span> Supporter
              </span>
              <span className="flex items-center gap-1">
                <span className="text-orange-400 drop-shadow-[0_0_5px_rgba(251,146,60,0.5)]">🕯️</span> 2-Week Supporter
              </span>
              <span className="flex items-center gap-1">
                <span className="text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.7)]">🕯️</span> Family Sponsor
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="candle-modal">
          <div className="bg-[#1C1C1C] border border-amber-900/30 rounded-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => { setShowModal(false); setSubmitted(false); }}
              className="absolute top-4 right-4 text-gray-500 hover:text-white"
              data-testid="close-candle-modal"
            >
              <X className="w-5 h-5" />
            </button>

            {submitted ? (
              <div className="text-center py-6" data-testid="candle-success">
                <div className="text-5xl mb-3 animate-bounce">🕯️</div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">Your Candle is Lit!</h3>
                <p className="text-sm text-gray-400">Thank you for showing your support.</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-5">
                  <span className="text-4xl">🕯️</span>
                  <h3 className="font-heading text-lg font-bold text-white mt-2">Light a Candle</h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Show your support for families in {programName}
                  </p>
                </div>

                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your name (optional)"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                    data-testid="candle-name-input"
                  />
                  <textarea
                    placeholder="Leave a message of support (optional)"
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    maxLength={300}
                    rows={3}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none resize-none"
                    data-testid="candle-message-input"
                  />

                  {/* Visibility Toggle */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">Display as:</span>
                    <div className="flex bg-white/5 rounded-lg p-0.5">
                      <button
                        onClick={() => setForm({...form, visibility: 'public'})}
                        className={`text-xs px-3 py-1.5 rounded-md transition-all ${
                          form.visibility === 'public' ? 'bg-amber-600 text-white' : 'text-gray-400'
                        }`}
                        data-testid="candle-public-toggle"
                      >
                        Public
                      </button>
                      <button
                        onClick={() => setForm({...form, visibility: 'anonymous'})}
                        className={`text-xs px-3 py-1.5 rounded-md transition-all ${
                          form.visibility === 'anonymous' ? 'bg-amber-600 text-white' : 'text-gray-400'
                        }`}
                        data-testid="candle-anonymous-toggle"
                      >
                        Anonymous
                      </button>
                    </div>
                  </div>

                  {/* Donation tie-in info */}
                  <div className="bg-amber-900/20 border border-amber-800/30 rounded-lg p-3">
                    <p className="text-xs text-amber-300/80">
                      Donated $70+? Your candle will glow gold on the wall as a Family Sponsor.
                    </p>
                  </div>
                </div>

                <button
                  onClick={lightCandle}
                  disabled={submitting}
                  className="w-full mt-4 bg-gradient-to-r from-amber-600 to-amber-500 text-white py-3 rounded-lg font-semibold text-sm hover:from-amber-500 hover:to-amber-400 transition-all disabled:opacity-50"
                  data-testid="submit-candle-btn"
                >
                  {submitting ? 'Lighting...' : '🕯️ Light My Candle'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Flicker animation */}
      <style>{`
        @keyframes flicker {
          0%, 100% { opacity: 1; transform: scale(1); }
          25% { opacity: 0.9; transform: scale(1.02); }
          50% { opacity: 1; transform: scale(0.98); }
          75% { opacity: 0.95; transform: scale(1.01); }
        }
      `}</style>
    </section>
  );
};

export default CandleWall;
